import React, { useState } from 'react';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PaginatedTemplate1 } from '@/components/ResumeTemplates/PaginatedTemplate1';
import { Template1ResumeData } from '@/components/ResumeTemplates/Template1';
import { toast } from 'sonner';
import { Upload, Eye, Settings } from 'lucide-react';

const ResumeDesign = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [resumeData, setResumeData] = useState<Template1ResumeData | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [margins, setMargins] = useState({ top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 });

  const handleLoadJson = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      setResumeData(parsed);
      setShowPreview(true);
      toast.success('Resume data loaded successfully');
    } catch (error) {
      toast.error('Invalid JSON format');
      console.error('JSON parse error:', error);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const parsed = JSON.parse(text);
        setJsonInput(text);
        setResumeData(parsed);
        setShowPreview(true);
        toast.success('Resume JSON file loaded successfully');
      } catch (error) {
        toast.error('Invalid JSON file');
        console.error('JSON parse error:', error);
      }
    };
    reader.readAsText(file);
  };

  const sampleData: Template1ResumeData = {
    Name: "John Doe",
    Phone: "(555) 123-4567",
    Email: "john.doe@email.com",
    City: "San Francisco",
    State: "CA",
    LinkedIn: "linkedin.com/in/johndoe",
    Summary: "Experienced software engineer with 10+ years developing scalable web applications. Proven track record of leading cross-functional teams and delivering high-impact solutions.",
    CareerHighlights: [
      {
        chtitle: "Technical Leadership",
        chdescription: "Led development of microservices architecture serving 5M+ users, reducing response times by 40%"
      },
      {
        chtitle: "Innovation",
        chdescription: "Pioneered AI-powered recommendation engine increasing user engagement by 65%"
      }
    ],
    ProfessionalExperience: [
      {
        company: "Tech Corp",
        location: "San Francisco, CA",
        job_title: "Senior Software Engineer",
        start_date: "Jan 2020",
        end_date: "Present",
        Descriptions: [
          { Description: "Architected and deployed cloud-native applications using AWS, reducing infrastructure costs by 30%" },
          { Description: "Mentored team of 5 junior engineers, establishing code review practices and development standards" },
          { Description: "Implemented CI/CD pipelines reducing deployment time from 2 hours to 15 minutes" }
        ]
      },
      {
        company: "Digital Solutions Inc",
        location: "San Jose, CA",
        job_title: "Software Engineer",
        start_date: "Mar 2017",
        end_date: "Dec 2019",
        Descriptions: [
          { Description: "Developed RESTful APIs serving 1M+ daily requests with 99.9% uptime" },
          { Description: "Collaborated with product team to define technical requirements for new features" },
          { Description: "Optimized database queries reducing page load times by 50%" }
        ]
      }
    ],
    Education: [
      {
        College_Name: "Stanford University",
        College_Degree_Name: "Bachelor of Science",
        Major: "Computer Science",
        City: "Stanford",
        ST: "CA",
        Year_Earned: "2017"
      }
    ],
    CertificationsAndLicenses: [
      {
        Certification_or_License_Name: "AWS Certified Solutions Architect",
        Institution_Organization: "Amazon Web Services",
        Year: "2021"
      }
    ],
    AwardsAndHonors: [
      {
        Award_Name: "Employee of the Year",
        Institution_Organization: "Tech Corp",
        Year: "2022"
      }
    ],
    Skills: [
      { Skill: "JavaScript" },
      { Skill: "TypeScript" },
      { Skill: "React" },
      { Skill: "Node.js" },
      { Skill: "Python" },
      { Skill: "AWS" },
      { Skill: "Docker" },
      { Skill: "Kubernetes" },
      { Skill: "PostgreSQL" },
      { Skill: "MongoDB" }
    ],
    VolunteerMemberships: [
      {
        PositionName: "Coding Mentor",
        InstitutionOrganization: "Code for Good",
        Year: "2020-Present"
      }
    ],
    Rules: "This resume demonstrates the measurement-then-placement pagination algorithm with intelligent content splitting and semantic awareness of resume structure."
  };

  const loadSampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setResumeData(sampleData);
    setShowPreview(true);
    toast.success('Sample data loaded');
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="Resume Design System"
        description="Measurement-then-placement pagination with intelligent content splitting"
        backTo="/"
      />

      <div className="container mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Panel */}
          <div className="space-y-4">
            <Card className="p-6">
              <div className="space-y-4">
                <div>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Load Resume Data
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="json-file">Upload JSON File</Label>
                      <Input
                        id="json-file"
                        type="file"
                        accept=".json"
                        onChange={handleFileUpload}
                        className="mt-2"
                      />
                    </div>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">Or</span>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="json-input">Paste JSON Data</Label>
                      <Textarea
                        id="json-input"
                        value={jsonInput}
                        onChange={(e) => setJsonInput(e.target.value)}
                        placeholder="Paste resume JSON data here..."
                        className="mt-2 font-mono text-xs h-64"
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={handleLoadJson} className="flex-1">
                        <Eye className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                      <Button onClick={loadSampleData} variant="outline">
                        Load Sample
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Pagination Settings
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="margin-top">Top Margin (in)</Label>
                  <Input
                    id="margin-top"
                    type="number"
                    step="0.1"
                    value={margins.top}
                    onChange={(e) => setMargins({ ...margins, top: parseFloat(e.target.value) })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="margin-bottom">Bottom Margin (in)</Label>
                  <Input
                    id="margin-bottom"
                    type="number"
                    step="0.1"
                    value={margins.bottom}
                    onChange={(e) => setMargins({ ...margins, bottom: parseFloat(e.target.value) })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="margin-left">Left Margin (in)</Label>
                  <Input
                    id="margin-left"
                    type="number"
                    step="0.1"
                    value={margins.left}
                    onChange={(e) => setMargins({ ...margins, left: parseFloat(e.target.value) })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="margin-right">Right Margin (in)</Label>
                  <Input
                    id="margin-right"
                    type="number"
                    step="0.1"
                    value={margins.right}
                    onChange={(e) => setMargins({ ...margins, right: parseFloat(e.target.value) })}
                    className="mt-2"
                  />
                </div>
              </div>
            </Card>

            {/* Technical Documentation */}
            <Card className="p-6 bg-muted/50">
              <h2 className="text-lg font-semibold mb-3">Pagination Architecture</h2>
              <div className="space-y-2 text-sm">
                <div>
                  <strong>Phase 1:</strong> Height Calculation
                  <p className="text-muted-foreground ml-4">
                    Budget = (11" - margins) × 96 DPI = 864px (with 1" margins)
                  </p>
                </div>
                <div>
                  <strong>Phase 2:</strong> Shadow DOM Measurement
                  <p className="text-muted-foreground ml-4">
                    Invisible clone → measure → destroy
                  </p>
                </div>
                <div>
                  <strong>Phase 3:</strong> State Machine
                  <p className="text-muted-foreground ml-4">
                    currentPage, currentHeight, maxHeight
                  </p>
                </div>
                <div>
                  <strong>Phase 4:</strong> Content Splitting
                  <p className="text-muted-foreground ml-4">
                    Recursive split until pieces fit
                  </p>
                </div>
                <div>
                  <strong>Phase 5:</strong> Experience Handling
                  <p className="text-muted-foreground ml-4">
                    First job atomic, others can split
                  </p>
                </div>
                <div>
                  <strong>Phase 6:</strong> Edge Cases
                  <p className="text-muted-foreground ml-4">
                    No empty pages, huge elements, accurate measurement
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Preview Panel */}
          <div className="lg:sticky lg:top-6">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Live Paginated Preview</h2>
              {showPreview && resumeData ? (
                <div className="border rounded-lg p-8 bg-gray-100 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                  <div className="flex flex-col items-center gap-4">
                    <PaginatedTemplate1 data={resumeData} />
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed rounded-lg p-12 text-center text-muted-foreground">
                  <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Load resume data to see paginated preview</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeDesign;
